import { useEffect, useState, useCallback } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { ReportExport } from "../../components/ReportExport";

interface LicensePoolReport {
  poolId: string;
  kind: string;
  totalLicenses: number;
  consumed: number;
  remaining: number;
  startsAt: string;
  endsAt: string;
}

interface TenantVoucher {
  id: string;
  code: string;
  title: string;
  catalog_item_id: string | null;
  book_title: string | null;
  max_uses: number;
  uses_count: number;
  starts_at: string | null;
  expires_at: string | null;
  is_active: number;
}

interface CatalogBookOption {
  id: string;
  title: string;
  slug: string;
  sku: string;
}

interface TenantConfigData {
  tenant: {
    id: string;
    slug: string;
    trade_name: string;
    legal_name: string;
    access_policy: string;
    wildcard_code_expires_at: string | null;
  };
  domains: { id: string; hostname: string; is_primary: number }[];
  availableBooks: CatalogBookOption[];
}

export function TenantAdminPage() {
  const [pools, setPools] = useState<LicensePoolReport[]>([]);
  const [vouchers, setVouchers] = useState<TenantVoucher[]>([]);
  const [config, setConfig] = useState<TenantConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Formulário de Novo Voucher
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherTitle, setVoucherTitle] = useState("");
  const [voucherBookId, setVoucherBookId] = useState("");
  const [voucherUses, setVoucherUses] = useState(25);
  const [creatingVoucher, setCreatingVoucher] = useState(false);

  // Configuração de Acesso Corporativo
  const [newWildcardCode, setNewWildcardCode] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [updatingConfig, setUpdatingConfig] = useState(false);

  // Importação de Colaboradores
  const [importText, setImportText] = useState("");
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const loadAllData = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get<{ pools: LicensePoolReport[] }>("/tenant/licenses/report").catch(() => ({ pools: [] })),
      apiClient.get<{ items: TenantVoucher[] }>("/tenant/vouchers").catch(() => ({ items: [] })),
      apiClient.get<TenantConfigData>("/tenant/config").catch(() => null),
    ])
      .then(([poolsData, vouchersData, configData]) => {
        setPools(poolsData.pools || []);
        setVouchers(vouchersData.items || []);
        setConfig(configData);
        if (configData?.availableBooks.length) {
          setVoucherBookId((prev) => prev || configData.availableBooks[0]?.id || "");
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar dados do painel."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  async function handleCreateVoucher(e: React.FormEvent) {
    e.preventDefault();
    setCreatingVoucher(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiClient.post("/tenant/vouchers", {
        code: voucherCode.trim().toUpperCase(),
        title: voucherTitle.trim(),
        catalogItemId: voucherBookId,
        maxUses: Number(voucherUses),
      });

      setSuccessMessage(`Voucher "${voucherCode.toUpperCase()}" criado com sucesso para sua equipe!`);
      setVoucherCode("");
      setVoucherTitle("");
      loadAllData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao criar código de voucher.");
    } finally {
      setCreatingVoucher(false);
    }
  }

  async function handleUpdateAccessConfig(e: React.FormEvent) {
    e.preventDefault();
    setUpdatingConfig(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiClient.patch("/tenant/config", {
        wildcardCode: newWildcardCode.trim() || undefined,
        institutionalDomain: newDomain.trim() || undefined,
      });

      setSuccessMessage("Configurações de acesso corporativo atualizadas com sucesso!");
      setNewWildcardCode("");
      setNewDomain("");
      loadAllData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao salvar configurações de acesso.");
    } finally {
      setUpdatingConfig(false);
    }
  }

  async function handleImport(e: React.FormEvent) {
    e.preventDefault();
    setImportMessage(null);
    try {
      const emails = importText
        .split(/[\n,;]/)
        .map((v) => v.trim())
        .filter(Boolean)
        .map((email) => ({ email }));
      const result = await apiClient.post<{ imported: number }>("/tenant/collaborators/import", { collaborators: emails });
      setImportMessage(`${result.imported} colaboradores importados com sucesso.`);
      setImportText("");
    } catch (err) {
      setImportMessage(err instanceof ApiError ? err.message : "Falha na importação de colaboradores.");
    }
  }

  async function handleLogout() {
    try {
      await apiClient.post("/tenant/auth/logout");
    } catch {
      // Ignora erro
    }
    localStorage.removeItem("datasci_tenant_token");
    localStorage.removeItem("datasci_tenant_user");
    window.location.href = "/login?perfil=corporate";
  }

  const userJson = typeof window !== "undefined" ? localStorage.getItem("datasci_tenant_user") : null;
  const user = userJson ? JSON.parse(userJson) : null;
  const companyName = config?.tenant.trade_name || user?.tenantTradeName || "Empresa Corporativa";

  return (
    <div style={{ padding: "0 var(--space-2)" }}>
      {/* Cabeçalho */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
        <div>
          <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "rgba(107, 33, 168, 0.12)", color: "#6b21a8", fontSize: "var(--text-xs)", fontWeight: 600, marginBottom: "var(--space-1)" }}>
            Portal de Gestão Corporativa
          </div>
          <h1 style={{ margin: 0 }}>{companyName}</h1>
          <p className="text-muted" style={{ margin: "var(--space-1) 0 0 0", fontSize: "var(--text-sm)" }}>
            Painel do gestor para disponibilização de obras por código, e-mail institucional e acompanhamento de licenças.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
            Gestor logado: <strong>{user?.username ?? "gestor"}</strong>
          </span>
          <button
            className="btn btn-secondary"
            style={{ fontSize: "var(--text-xs)", padding: "6px 12px" }}
            onClick={handleLogout}
          >
            Sair
          </button>
        </div>
      </div>

      {loading && <p className="text-muted" style={{ fontSize: "var(--text-xs)", marginBottom: "var(--space-2)" }}>Carregando dados da empresa...</p>}
      {successMessage && <div role="status" className="notice-success" style={{ marginBottom: "var(--space-4)", padding: "var(--space-3)", borderRadius: "var(--radius-sm)" }}>{successMessage}</div>}
      {error && <div role="alert" className="notice-error" style={{ marginBottom: "var(--space-4)", padding: "var(--space-3)", borderRadius: "var(--radius-sm)" }}>{error}</div>}

      {/* Grid de Configurações e Vouchers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "var(--space-5)", marginBottom: "var(--space-6)" }}>
        {/* Card 1: Códigos de Acesso e E-mail Institucional */}
        <div className="card">
          <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-2)", color: "var(--color-brass)" }}>
            1. Políticas de Acesso Corporativo
          </h2>
          <p className="text-muted" style={{ fontSize: "var(--text-xs)", marginBottom: "var(--space-4)" }}>
            Defina o código de acesso geral que seus colaboradores usam para desbloquear o catálogo e os domínios de e-mail institucional autorizados.
          </p>

          <div style={{ background: "var(--color-surface-sunken)", padding: "var(--space-3)", borderRadius: "var(--radius-sm)", marginBottom: "var(--space-4)", fontSize: "var(--text-xs)" }}>
            <div><strong>Código Corporativo Atual:</strong> <code>DEMO-CORP-2026</code></div>
            <div style={{ marginTop: 4 }}>
              <strong>Domínios Institucionais:</strong>{" "}
              {config?.domains.length ? config.domains.map((d) => `@${d.hostname}`).join(", ") : "Nenhum cadastrado"}
            </div>
          </div>

          <form onSubmit={handleUpdateAccessConfig}>
            <div className="field">
              <label htmlFor="new-wildcard">Atualizar Código Corporativo (Geral)</label>
              <input
                id="new-wildcard"
                type="text"
                placeholder="Ex.: DEMO-CORP-2026"
                value={newWildcardCode}
                onChange={(e) => setNewWildcardCode(e.target.value.toUpperCase())}
                style={{ textTransform: "uppercase" }}
              />
            </div>

            <div className="field" style={{ marginTop: "var(--space-3)" }}>
              <label htmlFor="new-domain">Adicionar Domínio Institucional Autorizado</label>
              <input
                id="new-domain"
                type="text"
                placeholder="Ex.: minhaempresa.com.br"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
              />
            </div>

            <button className="btn" type="submit" disabled={updatingConfig} style={{ marginTop: "var(--space-4)", width: "100%", fontSize: "var(--text-xs)" }}>
              {updatingConfig ? "Salvando..." : "Salvar Configuração de Acesso"}
            </button>
          </form>
        </div>

        {/* Card 2: Criar Voucher de Resgate de Obra */}
        <div className="card">
          <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-2)", color: "var(--color-brass)" }}>
            2. Gerar Voucher de Obra para Clientes / Colaboradores
          </h2>
          <p className="text-muted" style={{ fontSize: "var(--text-xs)", marginBottom: "var(--space-4)" }}>
            Gere um código de resgate com número fixo de usos para distribuir a clientes ou equipes específicas para download direto em PDF e EPUB.
          </p>

          <form onSubmit={handleCreateVoucher}>
            <div className="field">
              <label htmlFor="voucher-book">Obra Digital *</label>
              <select
                id="voucher-book"
                required
                value={voucherBookId}
                onChange={(e) => setVoucherBookId(e.target.value)}
              >
                {config?.availableBooks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title} ({b.sku})
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ marginTop: "var(--space-3)" }}>
              <label htmlFor="voucher-code-input">Código do Voucher *</label>
              <input
                id="voucher-code-input"
                type="text"
                required
                placeholder="Ex.: TIME-ENGENHARIA-2026"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                style={{ textTransform: "uppercase" }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
              <div className="field">
                <label htmlFor="voucher-title-input">Descrição / Destinatário</label>
                <input
                  id="voucher-title-input"
                  type="text"
                  required
                  placeholder="Ex.: Equipe de Dados"
                  value={voucherTitle}
                  onChange={(e) => setVoucherTitle(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="voucher-uses-input">Limite de Downloads</label>
                <input
                  id="voucher-uses-input"
                  type="number"
                  min={1}
                  max={500}
                  required
                  value={voucherUses}
                  onChange={(e) => setVoucherUses(Number(e.target.value))}
                />
              </div>
            </div>

            <button className="btn" type="submit" disabled={creatingVoucher} style={{ marginTop: "var(--space-4)", width: "100%", fontSize: "var(--text-xs)" }}>
              {creatingVoucher ? "Gerando voucher..." : "Gerar e Ativar Código de Download"}
            </button>
          </form>
        </div>
      </div>

      {/* Tabela de Vouchers Emitidos */}
      <section className="card" style={{ marginBottom: "var(--space-6)" }}>
        <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-3)" }}>Códigos e Vouchers Ativos da Empresa</h2>
        <div style={{ overflowX: "auto" }}>
          {vouchers.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "var(--text-sm)" }}>
              Nenhum código específico gerado ainda. Crie seu primeiro voucher no formulário acima.
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Obra Vinculada</th>
                  <th>Descrição</th>
                  <th className="num">Utilizados / Limite</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <code style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>{v.code}</code>
                    </td>
                    <td>{v.book_title || "Obra Geral"}</td>
                    <td className="text-muted">{v.title}</td>
                    <td className="num">
                      <strong>{v.uses_count}</strong> / {v.max_uses}
                    </td>
                    <td>
                      <span className="status-badge status-badge--paid">Ativo</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Licenças Corporativas e Importação */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "var(--space-5)" }}>
        <section className="card">
          <ReportExport title="Consumo do pool de licenças" subtitle={companyName} disabled={loading || !config}>
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th className="num">Total</th>
                    <th className="num">Consumidas</th>
                    <th className="num">Restantes</th>
                  </tr>
                </thead>
                <tbody>
                  {pools.map((pool) => (
                    <tr key={pool.poolId}>
                      <td>{pool.kind}</td>
                      <td className="num">{pool.totalLicenses}</td>
                      <td className="num">{pool.consumed}</td>
                      <td className="num">{pool.remaining}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && pools.length === 0 && <p className="text-muted">Nenhum pool de licenças disponível.</p>}
          </ReportExport>
        </section>

        <section className="card">
          <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-3)" }}>Pré-Cadastro de Colaboradores</h2>
          <form onSubmit={handleImport}>
            <div className="field">
              <label htmlFor="collaborators">E-mails institucionais (um por linha)</label>
              <textarea
                id="collaborators"
                rows={4}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="colaborador1@empresa-demo.com.br&#10;colaborador2@empresa-demo.com.br"
              />
            </div>
            {importMessage && <p role="status" className="notice-success" style={{ fontSize: "var(--text-xs)" }}>{importMessage}</p>}
            <button className="btn btn-secondary" type="submit" style={{ marginTop: "var(--space-3)", width: "100%" }}>
              Importar Lista de Colaboradores
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
